// BUILD-042 (Oct 19 06:28, item 2) — the reviewer's own entry to profile correction requests and the pending-work list behind them.
//
// WHY THIS EXISTS: HR Administration is gated on surfaces.hrAdmin (hr.org.manage or hr.grant.manage). A person who may review correction requests
// - for example a manager who holds hr.correction.review plus hr.team.view over their direct reports - is NOT an HR administrator and must not
// be handed the other HR Administration panels (employees, org, checklists, forms, access, activity log) to reach a review queue. This lite shell
// carries exactly two destinations: the correction queue and the pending-work list. It grants nothing: the server re-authorizes every request and
// shows only the requests whose subject AND fact the caller can already read (see correctionAccessFrom in the HR Lambda).
//
// An HR administrator never lands here: HrPage redirects /hr/corrections* to the existing HR Administration screens (same panels, same shell).
import { NavLink, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { useAdminAttention } from './attentionStore';
import { correctionBadgeFor } from './workspace/attentionModel';
import { AdminAttentionBanner } from './workspace/AttentionList';
import { AttentionBadge } from './workspace/AttentionBadge';
import { Breadcrumbs } from './workspace/Breadcrumbs';
import { SectionHeading } from './workspace/SectionHeading';
import { CorrectionRequestsPanel } from './CorrectionRequestsPanel';
import { PendingWorkPanel } from './PendingWorkPanel';

export function ReviewerWorkspace({ viewerEmployeeId }: { viewerEmployeeId: string }) {
  const location = useLocation();
  const attention = useAdminAttention();
  const badge = correctionBadgeFor(attention, 'Correction requests');
  const onPending = location.pathname.startsWith('/hr/corrections/pending');
  const highlight = new URLSearchParams(location.search).get('request') ?? undefined;
  const itemClass = (selected: boolean) =>
    `w-full flex items-center justify-between gap-2 text-left px-3 py-2 rounded-md text-sm mb-0.5 transition-colors block ${selected ? 'bg-[#EDF5FC] text-[#176A9B] font-medium' : 'text-gray-600 hover:bg-gray-50'}`;

  return (
    <div data-testid="reviewer-workspace">
      <Breadcrumbs items={[{ label: 'HR', to: '/hr/corrections' }, { label: 'Correction requests', to: onPending ? '/hr/corrections' : undefined }, ...(onPending ? [{ label: 'Pending work' }] : [])]} />
      <div className="flex flex-col sm:flex-row gap-5">
        <aside className="w-full sm:w-[183px] flex-shrink-0 space-y-3">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
            <div className="text-base font-bold text-gray-900 mb-0.5">Profile corrections</div>
            <div className="text-xs text-gray-500">Requests from people you can already see</div>
          </div>
          <nav aria-label="Correction review sections" className="bg-white rounded-lg border border-gray-200 shadow-sm p-2">
            <NavLink to="/hr/corrections" end className={() => itemClass(!onPending)} aria-current={!onPending ? 'page' : undefined} data-testid="reviewer-nav-requests">
              <span>Correction requests</span>
              <AttentionBadge view={badge} testId="reviewer-nav-badge" />
            </NavLink>
            <NavLink to="/hr/corrections/pending" className={() => itemClass(onPending)} aria-current={onPending ? 'page' : undefined} data-testid="reviewer-nav-pending">
              <span>Pending work</span>
            </NavLink>
          </nav>
        </aside>
        <main className="flex-1 min-w-0">
          {!onPending && <AdminAttentionBanner slice={attention} pendingPath="/hr/corrections/pending" reviewerView />}
          <Routes>
            <Route path="/" element={
              <>
                <SectionHeading title="Correction requests" subtitle="Review requested changes before official employee information changes." />
                <CorrectionRequestsPanel viewerEmployeeId={viewerEmployeeId} highlightRequestId={highlight} />
              </>
            } />
            <Route path="/pending" element={<PendingWorkPanel reviewerView />} />
            <Route path="*" element={<Navigate to="/hr/corrections" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
